/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useEffect, useState, useRef } from 'react';
import { validateMobileAsync } from '@api/APIServices';
import { Field, FieldProps, useFormikContext, getIn, ErrorMessage } from 'formik';
import { Alert, DropdownMenu, TextInput, CheckboxInputAccessible } from '@components';
import { iReviewFormValues } from '@services/ReviewService';
import gridFormStyles from '@pages/forms/Grid.module.scss';
import classnames from 'classnames';
import styles from './MobileField.module.scss';
import IssueIcon from '../../images/info.svg';
import { ReactComponent as PhoneIcon } from '../../images/phone.svg';

interface iProps extends FieldProps {
  title?: string;
  isMandatory: boolean;
  placeholder: string;
  obligationNumber: string;
  formName: string;
  hasPrimaryNumberType?: boolean;
  secondaryContact: boolean;
  hasSecondaryNumberType?: boolean;
  numberTypeMandatory?: boolean;
  noContactFieldName?: string;
  noContactFieldRequired?: boolean;
  currentSP?: string;
  displayError?: boolean;
  useFieldNameDirectly?: boolean;
  warningMessage: string;
  warningMessageSec: string;
}
const ContactInputWithExperian = ({
  field,
  title,
  noContactFieldRequired,
  noContactFieldName,
  currentSP,
  hasSecondaryNumberType,
  hasPrimaryNumberType,
  secondaryContact,
  numberTypeMandatory,
  isMandatory,
  placeholder = '',
  obligationNumber,
  formName,
  displayError,
  useFieldNameDirectly,
  warningMessage = '',
  warningMessageSec = '',
}: iProps): ReactElement => {
  const { errors, touched }: { errors: any; touched: any } = useFormikContext();
  const [disableContactlInput, setContactInput] = useState(false);
  // loading = true when the API call is running
  const [loading, setLoading] = useState(false);
  const [loadingSec, setLoadingSec] = useState(false);

  // API Call counter
  const [APICallCounter, setAPICallCounter] = useState(0);

  const [inputValue, setInputValue] = useState('');

  const [APICallCounterSec, setAPICallCounterSec] = useState(0);

  const [inputValueSec, setInputValueSec] = useState('');

  const hasError = getIn(errors, useFieldNameDirectly ? field.name : `${field.name}.ContactNumber`);
  const touchedError = getIn(touched, useFieldNameDirectly ? field.name : `${field.name}.ContactNumber`);

  const hasErrorSec = getIn(errors, `${field.name}.SecondaryContactNumber`);
  const touchedErrorSec = getIn(touched, `${field.name}.SecondaryContactNumber`);

  const formikContext = useFormikContext<any>();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRefSec = useRef<NodeJS.Timeout | null>(null);

  const CONTACT_OPTIONS = [
    { value: 'Mobile', display: 'Mobile' },
    { value: 'Work', display: 'Work' },
    { value: 'Home', display: 'Home' },
  ];

  const isValidMobile = (mobile: string): boolean => {
    const mobileRegex = /^\d{6,15}$/;
    return mobileRegex.test(mobile);
  };

  const callMobileAPI = async (
    currentCall: number,
    apiCounter: number,
    input: string,
    setLoadingFn: (arg0: boolean) => void,
    experianMessage: string,
  ) => {
    setLoadingFn(true);
    formikContext.setFieldValue(experianMessage, '');
    await validateMobileAsync(input, obligationNumber, formName).then((response) => {
      if (currentCall === apiCounter) {
        if (!response) {
          formikContext.setFieldTouched(useFieldNameDirectly ? field.name : `${field.name}.ContactNumber`, true);
        } else {
          formikContext.setFieldTouched(useFieldNameDirectly ? field.name : `${field.name}.ContactNumber`, true);
        }
        setLoadingFn(false);
        if ((response !== '') && currentCall === apiCounter) {
          formikContext.setFieldValue(experianMessage, response);
        } else {
          formikContext.setFieldValue(experianMessage, '');
        }
      }
    });
    setLoadingFn(false);
  };

  useEffect(() => {
    if (noContactFieldName) {
      setContactInput(getIn(formikContext.values, noContactFieldName));
    }
    let currentCall = APICallCounter;
    let debounceTimer;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (debounceTimer) clearTimeout(debounceTimer);
    timeoutRef.current = setTimeout(() => {
      if (isValidMobile(inputValue) && inputValue.length < 16) {
        debounceTimer = setTimeout(() => {
          callMobileAPI(currentCall, APICallCounter, inputValue, setLoading, warningMessage);
        }, 500);
      }
    }, 1000);
    return () => {
      currentCall = 0;
    };
  }, [inputValue, APICallCounter]);

  useEffect(() => {
    if (noContactFieldName) {
      setContactInput(getIn(formikContext.values, noContactFieldName));
    }
    let currentCallSec = APICallCounterSec;
    let debounceTimer;

    if (timeoutRefSec.current) {
      clearTimeout(timeoutRefSec.current);
    }

    if (debounceTimer) clearTimeout(debounceTimer);
    timeoutRef.current = setTimeout(() => {
      if (isValidMobile(inputValueSec) && (inputValueSec.length < 16)) {
        debounceTimer = setTimeout(() => {
          callMobileAPI(currentCallSec, APICallCounterSec, inputValueSec, setLoadingSec, warningMessageSec);
        }, 500);
      }
    }, 1000);
    return () => {
      currentCallSec = 0;
    };
  }, [inputValueSec, APICallCounterSec]);

  const handleChange = (e: any) => {
    formikContext.handleChange(e);
    setInputValue(e.target.value);
    setAPICallCounter((prevCount) => prevCount + 1);
  };

  const handleChangeSec = (e: any) => {
    formikContext.handleChange(e);
    setInputValueSec(e.target.value);
    setAPICallCounterSec((prevCount) => prevCount + 1);
  };

  return (
    <div className={gridFormStyles.form}>
      <div className={gridFormStyles.gridColHalf}>
        <div
          className={classnames({
            [styles.container]: true,
            [styles.titlePresent]: title,
          })}>
          {title && (
            <div
              className={classnames({
                [styles.title]: true,
                [styles.titleError]: hasError && touchedError,
              })}>
              <span className={styles.titleBold}>{title}</span>
              {!isMandatory && <span className={styles.optional}> ( optional )</span>}
            </div>
          )}
          <div className={styles.inputContainer}>
            <PhoneIcon className={styles.mobileIcon} />
            <input
              type="text"
              className={classnames({
                [styles.formInput]: true,
                [styles.inputError]: hasError && touchedError,
                [styles.disabled]: disableContactlInput,
              })}
              required={isMandatory}
              placeholder={placeholder}
              {...field}
              name={useFieldNameDirectly ? field.name : `${field.name}.ContactNumber`}
              // eslint-disable-next-line no-nested-ternary
              value={useFieldNameDirectly ? field.value : (field.value.ContactNumber ? field.value.ContactNumber : '')}
              onChange={handleChange}
              aria-label="Text Input"
              disabled={disableContactlInput}
            />
            {loading && (
              <div className={`${styles.cssloadcontainer} ${styles.inline}`}>
                <div className={styles.cssloadwhirlpool} />
              </div>
            )}
          </div>
          {displayError && <ErrorMessage className={styles.errorMsg} name={useFieldNameDirectly ? field.name : `${field.name}.ContactNumber`} component="span" />}
        </div>
        {hasPrimaryNumberType && (
          <Field name={`${field.name}.NumberType`} component={DropdownMenu} title="Number type" displayText={CONTACT_OPTIONS} defaultOption isMandatory={numberTypeMandatory} />
        )}
      </div>
      {noContactFieldRequired && currentSP && (
        <>
          <br />
          <Field
            name={noContactFieldName}
            component={CheckboxInputAccessible}
            title={currentSP}
            boxChecked={(e: boolean) => {
              setContactInput(e);
              formikContext.setFieldTouched(field.name, false);
              formikContext.setFieldValue(field.name, '');
              formikContext.setFieldValue(warningMessage, '');
            }}
          />
        </>
      )}
      {formikContext.values[warningMessage] && !hasError && (
        <Field
          name={warningMessage}
          component={Alert}
          theme="yellowInfo"
          icon={IssueIcon}
          message={formikContext.values[warningMessage]}
        />
      )}
      {secondaryContact && (
        <div className={gridFormStyles.gridColHalf}>
          <div
            className={classnames({
              [styles.container]: true,
              [styles.titlePresent]: title,
            })}>
            {title && (
              <div
                className={classnames({
                  [styles.title]: true,
                  [styles.titleError]: hasErrorSec && touchedErrorSec,
                })}>
                <span className={styles.titleBold}>Secondary phone number</span>
                <span className={styles.optional}> ( optional )</span>
              </div>
            )}
            <div>
              <div className={styles.inputContainer}>
                <PhoneIcon className={styles.mobileIcon} />
                <input
                  type="text"
                  className={classnames({
                    [styles.formInput]: true,
                    [styles.inputError]: hasErrorSec && touchedErrorSec,
                  })}
                  required={isMandatory}
                  placeholder={placeholder}
                  {...field}
                  name={`${field.name}.SecondaryContactNumber`}
                  value={field.value.SecondaryContactNumber ? field.value.SecondaryContactNumber : ''}
                  onChange={handleChangeSec}
                  aria-label="Text Input"
                />
                {loadingSec && (
                  <div className={`${styles.cssloadcontainer} ${styles.inline}`}>
                    <div className={styles.cssloadwhirlpool} />
                  </div>
                )}
              </div>
              {displayError && <ErrorMessage className={styles.errorMsg} name={`${field.name}.SecondaryContactNumber`} component="span" />}
            </div>
          </div>
          {secondaryContact && hasSecondaryNumberType && (
            <Field
              name={`${field.name}.SecondaryNumberType`}
              component={DropdownMenu}
              title="Number type"
              displayText={CONTACT_OPTIONS}
              defaultOption
              isMandatory={numberTypeMandatory}
              isOptional
            />
          )}
          {formikContext.values[warningMessageSec] && !hasErrorSec && (
            <Field
              name={warningMessageSec}
              component={Alert}
              theme="yellowInfo"
              icon={IssueIcon}
              message={formikContext.values[warningMessageSec]}
            />
          )}
        </div>
      )}
    </div>
  );
};

ContactInputWithExperian.defaultProps = {
  title: 'Contact phone number',
  hasPrimaryNumberType: true,
  numberTypeMandatory: true,
  hasSecondaryNumberType: true,
  noContactFieldRequired: true,
  currentSP: '',
  noContactFieldName: '',
  displayError: true,
  useFieldNameDirectly: false,
};

export default ContactInputWithExperian;
